import axios, { AxiosRequestConfig, AxiosResponse } from "axios"
import { JSONPath } from "jsonpath-plus"
import * as lodash from 'lodash'
import type { ContainerEnvVar, ExecuteContext, KubernetesOperator, KV, YamlTransformParam } from "yam-types"
import KubernetesOperatorClient from "../kube/kube-client"
import { createLogger, mergeObject, normalizeJsonPath } from "../util"

axios.defaults.timeout = 15000
axios.defaults.maxRedirects = 6

/**
 * Apply stage context data and methods, used by plugins
 */
export default class YamApplyContext implements ExecuteContext {

  dryRunMode: boolean

  /**
   * If force is true, the corresponding pod will be forcibly recreated
   */
  forceRestartWorkload: boolean

  k8sClient: KubernetesOperator

  containerEnvVariables: ContainerEnvVar[]

  log = createLogger('apply')

  _ = lodash

  managedK8SResources = new Map<string, KV>()
  yamlPatchQueue: (() => void)[] = []

  constructor(k8sClient: KubernetesOperatorClient, isDryRun: boolean, forceRestartWorkload: boolean, preDefinedEnvVars?: Map<string, string>) {
    this.dryRunMode = isDryRun
    this.forceRestartWorkload = forceRestartWorkload
    this.k8sClient = k8sClient
    this.containerEnvVariables = []
    preDefinedEnvVars?.forEach((v, k) => {
      this.containerEnvVariables.push({
        name: k,
        value: v
      })
    })
  }

  public mergeToYaml(param: YamlTransformParam): void {
    if (!param.content) {
      return
    }
    if (param.defer) {
      this.yamlPatchQueue.push(() => {
        this.mergeToYaml(Object.assign({}, param, { defer: false }))
      })
      return
    }

    if (!param.jsonPath) {
      const newContent = mergeObject(this.managedK8SResources.get(param.name) || {}, param.content, param.arrayReplaceMode)
      this.managedK8SResources.set(param.name, newContent as KV)
      this.log.info(`merged yaml content into '${param.name}'`)
    } else {
      if (!this.managedK8SResources.has(param.name)) {
        this.log.warn(`json path patching on NONE existing resource key: ${param.name}`)
        return
      }

      const jsonPathArr = typeof param.jsonPath === "string" ? [param.jsonPath] : param.jsonPath
      jsonPathArr.forEach(rawPath => {
        const jsonPath = normalizeJsonPath(rawPath, false)
        const patchObj = JSONPath({
          path: jsonPath,
          json: this.managedK8SResources.get(param.name) as KV
        })
        if (patchObj && patchObj.length > 0) {
          for (const obj of patchObj) {
            // object will be modified because of side-effect of _.mergeWith
            mergeObject(obj, param.content, param.arrayReplaceMode)
          }
        } else {
          if (param.failIfNotFound) {
            throw new Error(`can not patch on none existing json path '${rawPath}'`)
          } else {
            this.log.debug(`ignore merge yaml on path '${rawPath}' for '${param.name}' because sub-object is not found`)
          }
        }
      })
      this.log.debug(`merged yaml partial content into '${param.name}', path: ${jsonPathArr.join(',')}`)
    }
  }

  public removeFromYaml(param: YamlTransformParam): void {
    if (param.defer) {
      this.yamlPatchQueue.push(() => {
        this.removeFromYaml(Object.assign({}, param, { defer: false }))
      })
      return
    }
    if (!param.jsonPath) {
      this.managedK8SResources.delete(param.name)
      this.log.warn(`removing resource by key: ${param.name}`)
    } else {
      const jsonPathArr = typeof param.jsonPath === "string" ? [param.jsonPath] : param.jsonPath
      jsonPathArr.forEach(path => {
        const jsonPath = normalizeJsonPath(path, true)
        const patchTargetObj = JSONPath({
          path: jsonPath,
          json: this.managedK8SResources.get(param.name) as KV
        })
        if (!patchTargetObj || patchTargetObj.length === 0) {
          throw new Error(`can not find patching target for resource: ${param.name}, json path: ${path}`)
        }
        const idx = path.lastIndexOf('.')
        const tailProp = path.substring(idx + 1)
        if (/^[\w\d-_]+$/.test(tailProp)) {
          for (const obj of patchTargetObj) {
            delete obj[tailProp]
          }
          this.log.warn(`remove partial content of resource by json-path, delete '${tailProp}' from object path ${path.substring(0, idx)}.`)
        } else {
          this.log.error(`tailing wildcard(${tailProp}) expression is not supported when deleting with json path, no effect.`)
        }
      })
    }
  }

  public async sendRequest(option: AxiosRequestConfig): Promise<AxiosResponse> {
    // send request by axios and record logs
    this.log.info(`start send HTTP request [${option.method}] - ${option.url}`)
    if (this.log.isLevelEnabled('debug')) {
      this.log.debug(`request detail: ${JSON.stringify(this._.omit(option, 'data', 'auth', 'headers.Authorization', 'headers.authorization'))}`)
    }

    // if in dry-run mode, will not send real requests
    if (this.dryRunMode) {
      this.log.info('return mocked result in dry-run mode.')
      return {
        status: 202,
        statusText: 'Dry Run Mode - Accepted',
        data: 'None GET requests will not be sent in dry-run mode',
        headers: {},
        config: option
      }
    }

    const start = this._.now()
    try {
      this.log.debug(`start sending HTTP request to: ${option.url}`)
      const result = await axios.request(option)
      this.log.info(`finish HTTP request, status is ${result.status}, cost ${this._.now() - start}ms.`)
      this.logResponse4Debug(result)
      return result
    } catch (ex) {
      if (ex.response) {
        const result = ex.response as AxiosResponse
        this.logResponse4Debug(result)
        return result
      }
      throw ex
    }
  }

  public async applyPatchesAndManagedResources(): Promise<void> {
    const deferredPatches = this.yamlPatchQueue
    try {
      // a. handle deferred yaml patch functions
      if (deferredPatches.length > 0) {
        this.log.info(`---------------------------------------------`)
        this.log.info(`start applying ${deferredPatches.length} deferred yaml patches`)
        deferredPatches.forEach(patch => patch())
        this.log.info(`finish deferred yaml manipulation`)
      }

      // b. apply all yaml files to kubernetes cluster
      const resources = this.managedK8SResources
      if (resources.size > 0) {
        this.log.info(`---------------------------------------------`)
        this.log.info(`start applying ${resources.size} resources into kubernetes cluster`)
        await this.k8sClient.apply([...resources.values()])
        this.log.info(`finish synchronizing resources into kubernetes cluster`)
      }
    } finally {
      deferredPatches.splice(0, deferredPatches.length)
      this.managedK8SResources.clear()
    }
  }

  private logResponse4Debug(result: AxiosResponse) {
    if (this.log.isLevelEnabled('debug')) {
      let respDetail = JSON.stringify(result.data)
      if (respDetail.length > 2000) {
        respDetail = respDetail.substring(0, 2000) + '...'
      }
      this.log.debug(`response detail: ${JSON.stringify(this._.omit(result, 'config', 'request', 'data'))}, response data: ${respDetail}`)
    }
  }
}